/**
 * The shared findings lens (#1466 comment 48362, recipe 3).
 *
 * ── what a finding is, and why the contract leans on no one consumer ────────
 * Four surfaces were named as future adopters — validations on a priced
 * document, installer findings, policy checks, and operational health — and the
 * moment the contract borrows a word from any one of them the other three
 * either bend to it or fork. So a finding here is only the six things all four
 * genuinely share: a SEVERITY, a SOURCE and a CODE, a PLAIN-LANGUAGE MESSAGE,
 * optionally the RECORD/FIELD it is about, optionally ONE suggested governed
 * ACTION, and optionally a PROVENANCE TRACE. Nothing domain-shaped appears in
 * this file, deliberately: a `quantity` field or a `rule` object would be a
 * fifth adopter's problem on the day it arrives.
 *
 * ── severity is finite, and its ORDER is a fact this component owns ─────────
 * The order is NOT the caller's array order. A host that streams checks as they
 * finish, or concatenates two sources, will hand over an informational finding
 * before a critical one — and a panel that renders arrival order puts "3 items
 * were rounded" above "this cannot be submitted". The list is therefore sorted
 * by `FINDING_SEVERITIES` on every render, and the sort is STABLE (ES2019
 * guarantees it), so within one severity the caller's own order survives —
 * which is the half that IS the host's information: line order, evaluation
 * order, host discovery order.
 *
 * ── a suggested action is an OFFER, and the panel never performs it ─────────
 * The panel raises the action; the HOST runs it. That is not squeamishness
 * about side effects, it is the governance boundary: this component cannot
 * know what an act costs, cannot open a confirmation, and cannot write an audit
 * record — only the host can. What the panel owes is honesty about whether the
 * offer is real. An action the caller may not perform is rendered as a STATED
 * offer they cannot take, never as a live control: no disabled button that a
 * pointer user reads as "try harder" and a screen reader may skip entirely.
 * Hiding it outright was the other candidate and is worse — "there is a fix and
 * you may not apply it" is exactly the fact a governed surface exists to tell.
 *
 * ── the trace is a DRILL-DOWN, not prose ────────────────────────────────────
 * A provenance/rule trace is immutable, long, and mostly uninteresting until it
 * is the only interesting thing. Rendered inline it buries the finding it
 * explains — twenty findings each with a trace is a document, not a panel — so
 * it is disclosed, ONE AT A TIME, and `trace.render()` is not called at all
 * until it is opened. `onTraceOpen` exists because looking at provenance can
 * itself be an auditable read on some deployments; it is a NOTIFICATION, not a
 * control, and ignoring it changes nothing.
 *
 * ── empty is a real state, and it is three different states ─────────────────
 * "No findings" is the answer people are hoping for and it deserves to be said
 * plainly. It is not "the checks have not run yet" (`unchecked` — an absence of
 * knowledge), and it is not "you may not see the findings" (`refused` — an
 * absence of permission). Collapsing the three into one blank body tells a
 * person their document is clean when nothing has been evaluated, which is the
 * single most expensive lie this component could tell. Note that REFUSED here
 * is at PANEL scope; an action's refusal is at ROW scope, and they are separate
 * facts — you can be permitted to read every finding and permitted to fix none.
 *
 * ── controlled, and owning nothing ambient ──────────────────────────────────
 * The findings and their state arrive as props. This component reads no route,
 * no `localStorage`, no global context, and fetches nothing — it may be mounted
 * twice on one screen (two documents compared side by side), so anything
 * ambient would silently link two lenses meant to be independent. The one piece
 * of state it does keep is which trace is disclosed: transient view state
 * belonging to this mount, not a governed decision the host must own. A host
 * that needs to observe it gets `onTraceOpen`.
 *
 * ── the ruled numbers (#1465) ───────────────────────────────────────────────
 * Every interactive target is at least 44×44 and every glyph sits in the 20–22
 * band. Severity is never carried by colour alone: the severity WORD is
 * rendered beside the glyph, because a red dot is decoration and "Critical" is
 * information — and because the four adopters include operational health, where
 * the reader may well be colour-blind, on a projector, at 3am.
 */
import { useCallback, useId, useState, type ReactNode } from 'react'
import { readableError } from '@solum/format'
import s from './FindingsPanel.module.css'

/* What a refusal reads as when the host's own words were machine text (#3012). */
const REFUSED = 'You don’t have access to this.'

/**
 * The severities, MOST SEVERE FIRST. This tuple is the order — `severityRank`
 * and the sort both read it, so a new severity is added in one place and
 * cannot be added to a list without also being given a rank.
 *
 * Four rather than three: `critical` and `error` are not a redundancy across
 * the named adopters. Operational health distinguishes "the service is down"
 * from "one probe failed"; a validating document distinguishes "cannot be
 * submitted at all" from "this line is invalid". Merging them forces every
 * adopter that has both distinctions to encode the second one in prose.
 */
export const FINDING_SEVERITIES = ['critical', 'error', 'warning', 'info'] as const

export type FindingSeverity = (typeof FINDING_SEVERITIES)[number]

/**
 * The word shown beside the glyph. Exported so a host summarising elsewhere —
 * a rail badge, a toast — uses the same word this panel does rather than
 * inventing a synonym for the same severity two surfaces apart.
 */
export const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  critical: 'Critical',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
}

/** Decorative, `aria-hidden`, shape-coded so the four differ without colour. */
const SEVERITY_GLYPH: Record<FindingSeverity, string> = {
  critical: '◆',
  error: '✕',
  warning: '▲',
  info: 'ℹ',
}

/** What the finding is about. Locating is not acting — see `FindingAction`. */
export interface FindingSubject {
  /** What to call it out loud: "Line 3 — Widget A". */
  label: string
  /** The field within that record, when the finding is field-level. */
  field?: string
  /**
   * Take the person to it. Navigation only: it moves the eye, it does not
   * change anything, which is why it coexists with the one governed action
   * rather than counting as a second one. Omitted = the subject is plain text.
   */
  onSelect?: () => void
}

/**
 * ONE suggested governed action. Singular by type, not by convention: a row
 * offering three fixes is a menu, and a menu is a decision surface that wants
 * its own affordances, confirmation and audit — none of which belong to a lens.
 */
export interface FindingAction {
  /** Imperative and specific: "Recalculate price", not "Fix". */
  label: string
  /** The capability required. Omitted = the offer needs no capability. */
  capability?: string
  /** Plain language for WHY it is unavailable, shown instead of the control. */
  unavailableNote?: string
  /** The HOST performs it. The panel only offers. */
  run: () => void
}

/** The drill-down. `render` is lazy and is not called until it is opened. */
export interface FindingTrace {
  /** The disclosure's own label. Defaults to "Trace". */
  label?: string
  render: () => ReactNode
}

export interface Finding {
  /** Stable identity — list keys, test ids and the disclosure hang off it. */
  id: string
  severity: FindingSeverity
  /** Who says so: "Pricing rules", "Installer", "Policy". */
  source: string
  /** The stable machine code, if there is one: "PRICE_MISSING". */
  code?: string
  /** Addressed to a person. The code is not a message. */
  message: string
  subject?: FindingSubject
  action?: FindingAction
  trace?: FindingTrace
}

/**
 * The finite states, so a host cannot invent a seventh and so the three kinds
 * of "nothing here" stay three different sentences.
 */
export type FindingsState =
  /** The checks have not run. An absence of KNOWLEDGE, not of findings. */
  | { kind: 'unchecked'; message?: string }
  /** They are running now. */
  | { kind: 'loading' }
  /** They ran. Zero findings is legitimate and renders as the empty answer. */
  | { kind: 'ready'; findings: readonly Finding[] }
  /** They ran and found nothing — the answer people want, said plainly. */
  | { kind: 'empty'; message?: string }
  /** Some sources answered. What arrived is still worth showing. */
  | { kind: 'partial'; findings: readonly Finding[]; message: string }
  /** Authorisation said no. An absence of PERMISSION, not of findings. */
  | { kind: 'refused'; message: string }
  | { kind: 'error'; message: string }

export interface FindingsPanelProps {
  state: FindingsState
  /**
   * Capability keys the caller holds, or `null` for "this host has no
   * capability model". `null` means every offer stands, NOT that none do —
   * the distinction between an unmodelled host and a powerless one.
   */
  permitted?: readonly string[] | null
  /**
   * The accessible name of the region. No visible heading is rendered: this is
   * a lens dropped into a frame that already has a title — a workbench panel, a
   * drawer, a card — and a second heading inside it is a duplicate, not chrome.
   */
  label?: string
  /** Told when a trace is DISCLOSED. A notification, never a control. */
  onTraceOpen?: (finding: Finding) => void
  testId?: string
}

/** Position in `FINDING_SEVERITIES`; lower is worse. */
export function severityRank(severity: FindingSeverity): number {
  return FINDING_SEVERITIES.indexOf(severity)
}

/**
 * Severity order, caller order preserved inside a severity. A copy, because
 * sorting the caller's array in place would mutate a prop.
 */
export function sortFindings(findings: readonly Finding[]): readonly Finding[] {
  return [...findings].sort((a, b) => severityRank(a.severity) - severityRank(b.severity))
}

/** How many of each. Every severity is a key, including the zeroes. */
export function summariseFindings(
  findings: readonly Finding[],
): Record<FindingSeverity, number> {
  const counts: Record<FindingSeverity, number> = {
    critical: 0, error: 0, warning: 0, info: 0,
  }
  for (const f of findings) counts[f.severity] += 1
  return counts
}

/** The worst severity present, or `null` for an empty list. */
export function worstSeverity(findings: readonly Finding[]): FindingSeverity | null {
  let worst: FindingSeverity | null = null
  for (const f of findings) {
    if (worst === null || severityRank(f.severity) < severityRank(worst)) worst = f.severity
  }
  return worst
}

/**
 * May this caller take this offer? `null` permitted is "no capability model",
 * which permits — a host with no model showing nothing would be the same defect
 * as a host with no model showing everything, pointed the other way.
 */
export function canPerform(
  action: FindingAction, permitted: readonly string[] | null | undefined,
): boolean {
  if (!action.capability) return true
  if (permitted == null) return true
  return permitted.includes(action.capability)
}

/** "2 critical, 1 warning". Severity order, zeroes omitted. */
function summaryText(findings: readonly Finding[]): string {
  const counts = summariseFindings(findings)
  return FINDING_SEVERITIES
    .filter((sev) => counts[sev] > 0)
    .map((sev) => `${counts[sev]} ${SEVERITY_LABEL[sev].toLowerCase()}`)
    .join(', ')
}

export function FindingsPanel({
  state, permitted = null, label = 'Findings', onTraceOpen, testId = 'findings',
}: FindingsPanelProps) {
  const uid = useId()
  const [openTrace, setOpenTrace] = useState<string | null>(null)

  /* One trace at a time. Twenty open traces is the inline-prose failure this
     disclosure exists to avoid, reached one click at a time. */
  const toggleTrace = useCallback((finding: Finding) => {
    setOpenTrace((current) => {
      if (current === finding.id) return null
      onTraceOpen?.(finding)
      return finding.id
    })
  }, [onTraceOpen])

  /* `ready` with zero findings renders as the empty answer rather than a blank
     list. A host mapping an API response has no reason to branch on length, and
     the truthful reading of "the checks ran and returned nothing" IS empty. */
  const findings = state.kind === 'ready' || state.kind === 'partial' ? state.findings : []
  const sorted = sortFindings(findings)

  return (
    <section className={s.panel} aria-label={label} data-testid={testId}
      data-state={state.kind}>
      {state.kind === 'partial' && (
        <p className={s.note} data-kind="partial" role="status"
          data-testid={`${testId}-partial`}>{state.message}</p>
      )}

      {sorted.length > 0 && (
        <p className={s.summary} role="status" data-testid={`${testId}-summary`}>
          {summaryText(sorted)}
        </p>
      )}

      {sorted.length > 0 && (
        <ul className={s.list} data-testid={`${testId}-list`}>
          {sorted.map((f) => (
            <Row key={f.id} finding={f} permitted={permitted} testId={testId}
              traceId={`${testId}-trace-${uid}-${f.id}`}
              traceOpen={openTrace === f.id}
              onToggleTrace={() => toggleTrace(f)} />
          ))}
        </ul>
      )}

      {sorted.length === 0 && <Body state={state} testId={testId} />}
    </section>
  )
}

/** The bodyless states, each said as its own sentence — none of them a blank. */
function Body({ state, testId }: { state: FindingsState; testId: string }) {
  switch (state.kind) {
    case 'unchecked':
      /* Not empty. Nothing has been evaluated, and telling someone their
         document is clean when nothing looked at it is the worst thing here. */
      return <p className={s.note} data-kind="unchecked" data-testid={`${testId}-unchecked`}>
        {state.message ?? 'Checks have not run yet.'}
      </p>
    case 'loading':
      return <p className={s.note} role="status" data-testid={`${testId}-loading`}>Checking…</p>
    case 'refused':
      /* Not empty and not an error. The findings exist; this person may not
         read them, and saying which is the whole of being governed. */
      return <p className={s.note} data-kind="refused" role="status"
        data-testid={`${testId}-refused`}>{readableError(state.message, REFUSED)}</p>
    case 'error':
      return <p className={s.note} data-kind="error" role="alert"
        data-testid={`${testId}-error`}>{readableError(state.message)}</p>
    case 'ready':
    case 'empty':
    case 'partial':
      /* The good answer, and it is allowed to sound like one. */
      return <p className={s.note} data-kind="empty" data-testid={`${testId}-empty`}>
        {(state.kind === 'empty' && state.message) || 'No findings.'}
      </p>
  }
}

function Row({ finding, permitted, testId, traceId, traceOpen, onToggleTrace }: {
  finding: Finding
  permitted: readonly string[] | null
  testId: string
  traceId: string
  traceOpen: boolean
  onToggleTrace: () => void
}) {
  const { severity, source, code, message, subject, action, trace } = finding
  const subjectText = subject
    ? subject.field ? `${subject.label} · ${subject.field}` : subject.label
    : null

  return (
    <li className={s.row} data-severity={severity}
      data-testid={`${testId}-row-${finding.id}`}>
      <span className={s.mark} aria-hidden>{SEVERITY_GLYPH[severity]}</span>
      <div className={s.main}>
        <p className={s.message}>
          {/* The WORD, not only the colour. `data-severity` styles it; this text
              is what a screen reader and a colour-blind reader actually get. */}
          <span className={s.sev} data-severity={severity}>{SEVERITY_LABEL[severity]}</span>
          {message}
        </p>

        {/* Provenance of the CLAIM: who said it, under what code. Quiet, but
            present — "the installer says so" and "policy says so" are different
            findings even when the sentence is identical. */}
        <p className={s.meta} data-testid={`${testId}-meta-${finding.id}`}>
          {code ? `${source} · ${code}` : source}
        </p>

        {subjectText !== null && (
          subject?.onSelect
            ? <button type="button" className={s.subject} onClick={subject.onSelect}
              data-testid={`${testId}-subject-${finding.id}`}
              aria-label={`Go to ${subjectText}`}>{subjectText}</button>
            : <p className={s.subjectText}
              data-testid={`${testId}-subject-${finding.id}`}>{subjectText}</p>
        )}

        {action && (canPerform(action, permitted)
          ? <button type="button" className={s.action} onClick={action.run}
            data-testid={`${testId}-action-${finding.id}`}>{action.label}</button>
          /* Stated, not offered. Not a disabled button: disabled controls are
             skipped by some screen readers and read as "try again" by pointer
             users, and neither conveys that the offer is real but not theirs. */
          : <p className={s.locked} data-testid={`${testId}-action-locked-${finding.id}`}>
            <span className={s.lockedLabel}>{action.label}</span>
            {action.unavailableNote ?? 'You do not have permission to do this.'}
          </p>
        )}

        {trace && (
          <>
            <button type="button" className={s.traceToggle} onClick={onToggleTrace}
              aria-expanded={traceOpen} aria-controls={traceOpen ? traceId : undefined}
              data-testid={`${testId}-trace-toggle-${finding.id}`}>
              {trace.label ?? 'Trace'}
            </button>
            {/* Lazy on purpose: an unopened trace costs nothing, which is what
                makes a two-hundred-finding list affordable. */}
            {traceOpen && (
              <div className={s.trace} id={traceId}
                data-testid={`${testId}-trace-${finding.id}`}>{trace.render()}</div>
            )}
          </>
        )}
      </div>
    </li>
  )
}
